import React, { useCallback, useMemo } from 'react';
import styled from 'styled-components/macro';
import { func, string } from 'prop-types';

import AnimatedPath from '../AnimatedPath/AnimatedPath';
import { entityPropType } from '../LineChart/prop-types/entities';

const Path = styled(AnimatedPath)`
  fill: none;
  stroke-width: 2;
  stroke-linejoin: round;
  vector-effect: non-scaling-stroke;
  shape-rendering: auto;
`;

/**
 * MissingDataPath renders a dashed line at the end of trend lines where
 * data is missing.
 *
 * @param       {Object} props React props
 * @param       {Function} path d3-shape configured path generator
 * @param       {Object} trend Trend data to render an interpolation for
 * @param       {Object} presentational Other props that can be used to style
 *                                      the rendered path.
 * @constructor
 */
function MissingDataPath(props: any): JSX.Element | null {
  const { path, trend, valueKey, ...presentational } = props;

  /**
   * Use the path.defined() function that was added to the d3-shape
   * path generator to find undefined elements.
   *
   * @type {Boolean}
   */
  const hasData = useCallback(path.defined(), [path]);

  /**
   * Find the index of the first array element that has data.
   * @type {Number}
   */
  const idx = useMemo(() => {
    return trend.data.findIndex(hasData);
  }, [hasData, trend.data]);

  /**
   * If that index is 0, all elements have a non-null value. Don't render an
   * interpolation.
   */
  if (idx === 0) return null;

  /**
   * Construct an array of interpolated elements, by filling the elements
   * without values with the value of the first element that has data.
   * @type {Array}
   */
  const interpolatedTrend = trend.data.slice(0, idx + 1).map((d: any) => ({
    ...d,
    [valueKey]: trend.data[idx][valueKey],
  }));

  return (
    <Path
      pathString={path(interpolatedTrend)}
      strokeDasharray="4 4"
      opacity="0.6"
      {...presentational}
    />
  );
}

MissingDataPath.propTypes = {
  path: func.isRequired,
  trend: entityPropType.isRequired,
  valueKey: string.isRequired,
  stroke: string,
  strokeDasharray: string,
  opacity: string,
};

export default MissingDataPath;
